import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useIntersect } from '../hooks/useIntersect';
import { VideoBackground, VideoReel } from '../components/VideoComponents';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

const PILLARS = [
  { icon: '◐', label: 'Education & Training',   color: '#4e9cf5',  desc: 'Skills for the workforce of tomorrow' },
  { icon: '◉', label: 'Services & Consulting',  color: '#e8a43a',  desc: 'Digital transformation for businesses' },
  { icon: '◈', label: 'Products & Platforms',   color: '#3ecf8e',  desc: 'Software built for Africa' },
  { icon: '◎', label: 'Media & Communication',  color: '#f0a83a',  desc: 'Storytelling and digital media' },
  { icon: '⬡', label: 'Innovation & Research',  color: '#a78bfa',  desc: 'Advancing technology on the continent' },
];

const GOVERNANCE = [
  { title: 'Founder-Led',            icon: '◉', desc: 'Strategic vision and core identity permanently anchored with the founder. No external override.' },
  { title: 'Constitution-Protected', icon: '◈', desc: 'A living governance document defines Class A, B, and C change categories — protecting the mission.' },
  { title: 'Capital-Flexible',       icon: '◐', desc: 'Structured to welcome investment and partnerships while maintaining sovereign decision-making power.' },
];

function FadeSection({ children, delay = 0 }) {
  const [ref, visible] = useIntersect();
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(24px)', transition: `opacity 0.6s ${delay}s ease, transform 0.6s ${delay}s ease` }}>
      {children}
    </div>
  );
}

function EntityCard({ entity, delay = 0 }) {
  const coverUrl = resolveMedia(entity.media_url || entity.logo_url);
  return (
    <FadeSection delay={delay}>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {coverUrl && (
          <div style={{ height: 130, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
            <img src={coverUrl} alt={entity.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => { e.target.parentNode.style.display = 'none'; }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(8,10,13,0.7) 0%, transparent 60%)' }} />
            <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6 }}>
              <span className="badge badge-gold" style={{ fontSize: '0.6rem', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>{entity.brand_class?.replace('_', ' ')}</span>
              <span className="badge badge-dim"  style={{ fontSize: '0.6rem', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>{entity.type}</span>
            </div>
          </div>
        )}
        <div style={{ padding: '20px 24px 24px' }}>
          {!coverUrl && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span className="badge badge-gold" style={{ fontSize: '0.6rem' }}>{entity.brand_class?.replace('_', ' ')}</span>
              <span className="badge badge-dim"  style={{ fontSize: '0.6rem' }}>{entity.type}</span>
            </div>
          )}
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, marginBottom: 8 }}>{entity.name}</h3>
          {entity.purpose && <p className="body-sm" style={{ color: 'var(--t-secondary)', lineHeight: 1.6 }}>{entity.purpose}</p>}
          {entity.target_audience && <div style={{ marginTop: 10, fontSize: '0.75rem', color: 'var(--t-muted)' }}>Audience: {entity.target_audience}</div>}
        </div>
      </div>
    </FadeSection>
  );
}

export default function AboutPage() {
  const [entities, setEntities] = useState([]);
  useEffect(() => { publicAPI.ecosystem({ limit: 12 }).then(r => setEntities(r.data?.data || [])).catch(() => {}); }, []);

  return (
    <div>
      {/* ── HERO with video background ─────────────────── */}
      <VideoBackground
        slot="about_hero_video"
        overlayGrad="linear-gradient(135deg, rgba(8,10,13,0.85) 0%, rgba(8,10,13,0.5) 60%, rgba(232,164,58,0.06) 100%)"
        style={{ paddingTop: 'calc(var(--nav-h) + clamp(60px,8vw,120px))', paddingBottom: 'clamp(80px,10vw,140px)', position: 'relative', overflow: 'hidden' }}
      >
        <div style={{ position: 'absolute', right: '-10%', top: '10%', fontFamily: 'var(--font-display)', fontSize: 'clamp(160px,30vw,400px)', fontWeight: 700, lineHeight: 1, color: 'rgba(232,164,58,0.03)', userSelect: 'none', pointerEvents: 'none', letterSpacing: '-0.05em', zIndex: 0 }}>MH</div>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="section-label label" style={{ marginBottom: 24, animation: 'fadeUp 0.6s ease both' }}>The Ecosystem</div>
          <h1 className="display-xl" style={{ maxWidth: 720, animation: 'fadeUp 0.6s 0.1s ease both' }}>
            One Hub.<br /><span style={{ color: 'var(--gold)' }}>Five Pillars.</span><br />Infinite Possibilities.
          </h1>
          <p className="body-lg" style={{ color: 'var(--t-secondary)', maxWidth: 560, marginTop: 28, animation: 'fadeUp 0.6s 0.2s ease both' }}>
            MilleHills Ltd is not just a company — it's a living ecosystem. Built from the hills of Gisenyi, Rwanda, to serve Africa and the world.
          </p>
        </div>
      </VideoBackground>

      {/* ── MISSION + REEL ────────────────────────────────── */}
      <section style={{ padding: 'var(--section) 0', background: 'var(--bg-1)', borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <FadeSection>
              <div className="section-label label" style={{ marginBottom: 20 }}>Mission & Vision</div>
              <h2 className="display-md" style={{ marginBottom: 24 }}>Built for Africa.<br /><span style={{ color: 'var(--gold)' }}>Connected to the World.</span></h2>
              <p className="body-lg" style={{ color: 'var(--t-secondary)', marginBottom: 20 }}>We believe technology is the greatest equalizer. Our mission is to build the infrastructure — human and digital — that enables Africa's next chapter.</p>
              <p className="body-md" style={{ color: 'var(--t-secondary)', opacity: 0.7 }}>From training the next wave of developers and designers, to delivering enterprise-grade digital services, to creating media that tells Africa's story on its own terms — MilleHills is the ecosystem that makes it possible.</p>
            </FadeSection>
            <FadeSection delay={0.15}>
              <VideoReel slot="about_reel" title="About MilleHills" label="Our Story" aspectRatio="16/9" borderRadius={14} />
            </FadeSection>
          </div>
        </div>
      </section>

      {/* ── QUICK FACTS ───────────────────────────────────── */}
      <section style={{ padding: 'clamp(40px,5vw,70px) 0', background: 'var(--bg-0)' }}>
        <div className="container">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { q: 'Where?',   a: 'Gisenyi, Rubavu District, Western Province, Rwanda' },
              { q: 'Founded?', a: '2024 — Constitution-protected from day one' },
              { q: 'Focus?',   a: 'Technology, Innovation, Education, Media, and Research' },
              { q: 'Who?',     a: 'Youth, businesses, communities, and institutions across Africa' },
            ].map((item, i) => (
              <div key={item.q} style={{ display: 'flex', gap: 20, padding: '18px 22px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', opacity: 0, animation: `fadeUp 0.5s ${i * 0.08}s ease forwards` }}>
                <div style={{ width: 60, flexShrink: 0, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gold)', paddingTop: 2 }}>{item.q}</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--t-secondary)', lineHeight: 1.5 }}>{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PILLARS ───────────────────────────────────────── */}
      <section style={{ padding: 'var(--section) 0', background: 'var(--bg-1)', borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <FadeSection>
            <div className="section-label label" style={{ marginBottom: 20 }}>Structure</div>
            <h2 className="display-md" style={{ marginBottom: 48 }}>The Five Pillars</h2>
          </FadeSection>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
            {PILLARS.map((p, i) => (
              <FadeSection key={p.label} delay={i * 0.06}>
                <div style={{ padding: '28px 20px', textAlign: 'center', background: 'var(--bg-card)', border: `1px solid ${p.color}22`, borderRadius: 'var(--r-lg)', borderTop: `3px solid ${p.color}`, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 28, color: p.color }}>{p.icon}</span>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.3 }}>{p.label}</div>
                  <p style={{ fontSize: '0.78rem', color: 'var(--t-muted)', lineHeight: 1.6 }}>{p.desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── BRANDS & ENTITIES ─────────────────────────────── */}
      <section id="brands" style={{ padding: 'var(--section) 0', background: 'var(--bg-0)' }}>
        <div className="container">
          <FadeSection>
            <div className="section-label label" style={{ marginBottom: 20 }}>Brands & Entities</div>
            <h2 className="display-md" style={{ marginBottom: 48 }}>The Ecosystem Family</h2>
          </FadeSection>
          {entities.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
              {entities.map((e, i) => <EntityCard key={e.id} entity={e} delay={i * 0.05} />)}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
              {[
                { name: 'MilleHills Core', class: 'Core',      type: 'Brand',     color: '#e8a43a', icon: 'M',  desc: 'The central hub — strategy, governance, and operational backbone of the entire ecosystem.' },
                { name: 'MARA Matsiko',    class: 'Sub-Brand', type: 'Media',     color: '#2dd4bf', icon: '◎', desc: 'Media and communications sub-brand producing digital content, storytelling, and PR for the ecosystem.' },
                { name: 'BayBees 2.1',    class: 'Sub-Brand', type: 'Education', color: '#f472b6', icon: '◈', desc: "Youth-focused creative and educational content brand nurturing the next generation of African innovators." },
              ].map((e, i) => (
                <FadeSection key={e.name} delay={i * 0.08}>
                  <div className="card" style={{ padding: '28px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: `${e.color}18`, border: `1px solid ${e.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: e.color }}>{e.icon}</div>
                      <div>
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>{e.name}</div>
                        <div className="label" style={{ color: 'var(--t-muted)', marginTop: 2 }}>{e.class} · {e.type}</div>
                      </div>
                    </div>
                    <p className="body-sm" style={{ color: 'var(--t-secondary)', lineHeight: 1.65 }}>{e.desc}</p>
                  </div>
                </FadeSection>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── GOVERNANCE with video bg ──────────────────────── */}
      <VideoBackground
        slot="about_hero_video"
        overlayGrad="linear-gradient(180deg, rgba(8,10,13,0.92) 0%, rgba(8,10,13,0.65) 50%, rgba(8,10,13,0.92) 100%)"
        style={{ padding: 'var(--section) 0', borderTop: '1px solid var(--border)' }}
      >
        <div className="container">
          <FadeSection>
            <div className="section-label label" style={{ marginBottom: 20 }}>Governance Model</div>
            <h2 className="display-md" style={{ marginBottom: 12 }}>Built to Last</h2>
            <p className="body-lg" style={{ color: 'var(--t-secondary)', maxWidth: 500, marginBottom: 48 }}>The Ecosystem Constitution defines how MilleHills grows, adapts, and stays true to its mission.</p>
          </FadeSection>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {GOVERNANCE.map((g, i) => (
              <FadeSection key={g.title} delay={i * 0.1}>
                <div style={{ padding: '32px 28px', background: 'rgba(13,15,18,0.7)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--r-lg)', borderBottom: '3px solid var(--gold)' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(232,164,58,0.1)', border: '1px solid rgba(232,164,58,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: 'var(--gold)', marginBottom: 16 }}>{g.icon}</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, marginBottom: 10 }}>{g.title}</div>
                  <p className="body-sm" style={{ color: 'var(--t-secondary)', lineHeight: 1.65 }}>{g.desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </VideoBackground>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(60px,8vw,100px) 0', background: 'var(--bg-1)', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
          <div>
            <h2 className="display-sm" style={{ marginBottom: 8 }}>Join the Ecosystem</h2>
            <p style={{ color: 'var(--t-secondary)', fontSize: '0.95rem' }}>Partner, volunteer, enroll, or build with us.</p>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/contact?type=partner" className="btn btn-primary">Partner With Us</Link>
            <Link to="/services"             className="btn btn-outline">Explore Services</Link>
          </div>
        </div>
      </section>
    </div>
  );
}